import React from "react";
import { useRouteMatch, Link, useParams, useLocation } from "react-router-dom";
import { useAuthState } from "../../../../components/AuthProvider";
import formattedDate from "../../../../utils/formattedDate";
import Container from "../../../../components/Container";
import Content from "../../../../components/Content";
import Flex from "../../../../components/Flex";
import Layout from "../../../../components/Layout";
import InnerTableComponent from "./InnerTableComponent";
import DefaultBreadcrumbs from "../../../../components/DefaultBreadcrumbs";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Breadcrumb from "../../../../components/Breadcrumbs";
import ButtonList from "../../../../components/ButtonList";
import Button from "../../../../components/Button";
import LinkButton from "../../../../components/LinkButton";
import Icon from "../../../../components/Icon";
import { checkReadOnlyUser } from "../../../../utils/checkReadOnlyUser.js";
import { formattedDateWithDefault } from "../../../../utils/formattedDate";
import {
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaFingerprint,
  FaStethoscope,
  FaHistory,
} from "react-icons/fa";
import { MdList } from "react-icons/md";
import { isImdmClient } from "../../../../utils/isImdmClient";
import styles from "./CompareRecords.module.scss";
import "../../../../components/PopOver/style.css";
import { FastField } from "formik";
import Modal from "../../../../components/ModalPopup";
import {
  fetchReviewQueueCountData,
  fetchBulkOrgData,
  fetchClientSubscriptionsData,
  aggregatesCall,
} from "../../../../utils/fetchData";

const CompareRecords = ({
  overlayRecordsData = null,
  isQuarantineRecord = false,
  clientId,
  recordOverrideHandler,
  isRecordOverridden = false,
  focusIdentityButtonHandler,
  skipAndReviewHandler,
  applyAndResolveOverlayRecordHandler,
  fromSearchOverlay = false,
  disableApplyChanges = false,
}) => {
  const { user } = useAuthState();
  const { page } = useParams();
  const { selectedClient } = user.preferences;
  const isImdm = isImdmClient(selectedClient);
  const [zoomValue, setZoomValue] = React.useState(1);
  const [zoomWidth, setZoomWidth] = React.useState(15);
  const [showIdentifiers, setShowIdentifiers] = React.useState(false); // collapsed by default
  const [quarantineConfirmDialog, setQuarantineConfirmDialog] =
    React.useState(false);
  // let array = [];
  const [array, setArray] = React.useState([]);

  const [isOverlayRecordPage, setIsOverlayRecordPage] = React.useState(
    overlayRecordsData ? true : false,
  );

  React.useEffect(() => {
    if (!isOverlayRecordPage) {
      const compareRaw = localStorage.getItem("compare");
      if (compareRaw) {
        const compareList = JSON.parse(compareRaw);
        const map = new Map(compareList[page]);
        const newArray = [];
        [...map].forEach(([, record]) => {
          newArray.push(record);
        });
        setArray(newArray);
        console.log("Updated array:", newArray);
      } else {
        setArray([]);
        console.warn("No compare data found in localStorage.");
      }
    } else if (overlayRecordsData) {
      // Handle overlay records
      setArray(overlayRecordsData);
      console.log("Updated array for overlay records:", overlayRecordsData);
    }
  }, [isOverlayRecordPage, overlayRecordsData, page]);

  const lastUpdatedTimeCell = (updateDate) => {
    if (updateDate === "Current") return updateDate;
    return updateDate ? formattedDateWithDefault(updateDate) : "";
  };

  console.log("**********");
  console.log(array);
  console.log("**********");

  const location = useLocation();

  // Determine if this is a repartition preview page
  const isRepartitionPreview = location.pathname.includes(
    "repartition-preview",
  );

  React.useEffect(() => {
    if (isRepartitionPreview) {
      setIsOverlayRecordPage(true);
    }
  }, [isRepartitionPreview]);

  const [beforeRepartitionRecords, setBeforeRepartitionRecords] =
    React.useState([]);
  const [afterRepartitionData, setAfterRepartitionData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  // Only poll localStorage for repartition data if this is a repartition preview page
  React.useEffect(() => {
    if (!isRepartitionPreview) {
      setIsLoading(false);
      return;
    }

    let retryCount = 0;
    const maxRetries = 10;
    const interval = setInterval(() => {
      const beforeData = localStorage.getItem("beforeRepartitionData");
      const afterData = localStorage.getItem("afterRepartitionData");

      if (beforeData && afterData) {
        const parsedBeforeData = JSON.parse(beforeData);
        const parsedAfterData = JSON.parse(afterData);
        setBeforeRepartitionRecords(() => {
          const entries = parsedBeforeData?.entry || [];
          return entries.map((e) => e.resource);
        });
        setAfterRepartitionData(parsedAfterData);
        setIsLoading(false); // Stop loading once data is available
        clearInterval(interval); // Stop polling
      } else {
        retryCount++;
        if (retryCount >= maxRetries) {
          console.warn("Max retries reached. Stopping polling.");
          clearInterval(interval); // Stop polling after max retries
          setIsLoading(false); // Ensure loading state is reset
        }
      }
    }, 500); // Poll every 500ms

    return () => clearInterval(interval); // Cleanup on unmount
  }, [isRepartitionPreview]);

  // 1. Parse AfterRepartition recordUpdates
  const [afterRepartitionMap, setAfterRepartitionMap] = React.useState({});
  const [afterRepartitionHasUpdates, setAfterRepartitionHasUpdates] =
    React.useState(true);
  const [patients, setPatients] = React.useState([]);

  // Extract golden record (first entry)
  const goldenRecord = beforeRepartitionRecords[0];

  const searchedGoldenId = goldenRecord?.id;

  React.useEffect(() => {
    if (!isRepartitionPreview || !afterRepartitionData) {
      return;
    }

    const updates = afterRepartitionData?.data?.recordUpdates || [];
    setAfterRepartitionHasUpdates(updates.length > 0);

    const patients = updates
      .map((update) => {
        try {
          const demo = JSON.parse(update.demographics || "{}");
          demo._golden_id = update.golden_id;
          return {
            source_entity_id: update.source_entity_id,
            patient: demo,
          };
        } catch (error) {
          console.error("Error parsing demographics:", error);
          return null; // Handle parsing errors gracefully
        }
      })
      .filter((item) => item !== null); // Remove null entries caused by errors

    setPatients(patients);
    const afterMap = {};
    patients.forEach(({ patient }) => {
      if (patient && patient.id) {
        afterMap[patient.id] = patient;
      }
    });
    setAfterRepartitionMap(afterMap);
  }, [afterRepartitionData, isRepartitionPreview]);

  // Track which afterRepartitionMap keys have been matched
  const matchedAfterIds = new Set();

  // Extract actual records (skip the first entry)
  const recordEntries = beforeRepartitionRecords.filter((record) => {
    if (!record || !record.id) return false;
    if (record.id === searchedGoldenId) return false; // skip golden record itself
    // Only include if link[].other.reference matches searchedGoldenId
    if (Array.isArray(record.link)) {
      return record.link.some(
        (l) => l.other && l.other.reference === searchedGoldenId,
      );
    }
    return false;
  });

  if (isQuarantineRecord) {
    // Handle quarantine records independently
    if (array.length === 0) {
      return <div>No quarantine records to compare</div>;
    }
  }

  const getPreferredName = (record, isGolden = false) => {
    if (!record?.name || record.name.length === 0) return "";
    // For golden record, always use the first name (survived)
    if (isGolden) {
      const nameObj = record.name[0];
      return `${
        nameObj.given ? nameObj.given.join(" ") : ""
      } ${nameObj.family || ""}`.trim();
    }
    // For other records, prefer name with attrCode === "PERCHSNNAME"
    const chosen = record.name.find(
      (n) =>
        n.extension &&
        n.extension.some(
          (e) =>
            e.url &&
            e.url.includes("attrCode") &&
            e.valueString === "PERCHSNNAME",
        ),
    );
    const nameObj = chosen || record.name[0];
    return `${nameObj.given ? nameObj.given.join(" ") : ""} ${
      nameObj.family || ""
    }`.trim();
  };

  const sources = [];
  const familyNames = [];
  const givenNames = [];
  const nameUse = [];
  const addressLines1 = [];
  const addressLines2 = [];
  const cities = [];
  const postalCodes = [];
  const addressUse = [];
  const stateCode = [];
  const genders = [];
  const DOBs = [];
  const phones = [];
  const phoneUse = [];
  const emails = [];
  const emailUse = [];
  const SSNs = [];
  const MBIs = [];
  const HICNs = [];
  const MRNs = [];
  const CPINs = [];
  const HSIDs = [];
  const allSaversAlternateMemberIds = [];
  const driversLicenseNumber = [];
  const sierraFacetsMemberIds = [];
  const gpsHouseholdIds = [];
  const pacificareBehaviorMemberIds = [];
  const riverValleyMemberIds = [];
  const alternateMemberId = [];
  const briovaExchangeId = [];
  const camsAlternateMemberId = [];
  const camsMemberId = [];
  const cdbXrefIds = [];
  const cesAlternateMemberId = [];
  const compasId = [];
  const compasMemberNumber = [];
  const cspFacetsAlternateMemberId = [];
  const employeeId = [];
  const employerAssignedId = [];
  const gpsIndividualIds = [];
  const icueMemberId = [];
  const irisAccountNumber = [];
  const niceMemberId = [];
  const optumClinicalServicesAlternateId = [];
  const optumHealthBehavioralServicesAlternateMemberId = [];
  const oxfordMemberId = [];
  const peoplesHealthMemberId = [];
  const personId = [];
  const primeAlternateMemberId = [];
  const rxClaimsMemberId = [];
  const rxClaimsSupplementalId = [];
  const sourceMemberId = [];
  const studentResourcesAlternateMemberId = [];
  const studentResourcesId = [];
  const umrAlternateMemberId = [];
  const ursulaId = [];
  const policyNum = [];
  const dependentCd = [];
  const divisionCd = [];
  const relationshipCd = [];
  const familyID = [];
  const memberID = [];
  const subscriberID = [];
  const carrierID = [];
  const accountID = [];
  const groupID = [];
  const origSysId = [];
  const medicaidRN = [];
  const coverageEndDate = [];
  const lastUpdated = [];

  for (const record of array) {
    let otherIdsString = "";
    let otherIdsCount = 0;

    if (record.meta.source && record.meta.source.length > 0) {
      sources.push(record.meta.source ? [record.meta.source] : []);
    } else {
      sources.push([]);
    }
    if (record.name && record.name.length > 0) {
      const filteredName = record.name.filter((name) => name.use !== "old");
      familyNames.push(
        filteredName.map((name) => (name.family ? name.family : "")),
      );
      givenNames.push(
        filteredName.map((name) => (name.given ? name.given.join(" ") : "")),
      );
      nameUse.push(filteredName.map((name) => (name.use ? name.use : "")));
    } else {
      familyNames.push([]);
      givenNames.push([]);
      nameUse.push([]);
    }

    if (record.address && record.address.length > 0) {
      const filteredAddress = record.address.filter(
        (address) => address.use !== "old",
      );
      addressLines1.push(
        filteredAddress.map((address) =>
          address.line && address.line.length > 0 ? address.line[0] : "",
        ),
      );
      addressLines2.push(
        filteredAddress.map((address) =>
          address.line && address.line.length > 1 ? address.line[1] : "",
        ),
      );
      cities.push(
        filteredAddress.map((address) => (address.city ? address.city : "")),
      );
      postalCodes.push(
        filteredAddress.map((address) =>
          address.postalCode ? address.postalCode : "",
        ),
      );
      addressUse.push(
        filteredAddress.map((address) => (address.use ? address.use : "")),
      );
      stateCode.push(
        filteredAddress.map((address) => (address.state ? address.state : "")),
      );
    } else {
      addressLines1.push([]);
      addressLines2.push([]);
      cities.push([]);
      postalCodes.push([]);
      addressUse.push([]);
      stateCode.push([]);
    }

    if (record.telecom && record.telecom.length > 0) {
      const filteredTelecom = record.telecom.filter(
        (telecom) => telecom.use !== "old",
      );
      phones.push(
        filteredTelecom
          .filter((tel) => tel.system === "phone" && tel.value)
          .map((tel) =>
            tel.system === "phone" ? (tel.value ? tel.value : "") : "",
          ),
      );
      phoneUse.push(
        filteredTelecom
          .filter((tel) => tel.system === "phone" && tel.value)
          .map((tel) =>
            tel.system === "phone" ? (tel.use ? tel.use : "") : "",
          ),
      );
      emails.push(
        filteredTelecom
          .filter((tel) => tel.system === "email" && tel.value)
          .map((tel) =>
            tel.system === "email" ? (tel.value ? tel.value : "") : "",
          ),
      );
      emailUse.push(
        filteredTelecom
          .filter((tel) => tel.system === "email" && tel.value)
          .map((tel) =>
            tel.system === "email" ? (tel.use ? tel.use : "") : "",
          ),
      );
    } else {
      phones.push([]);
      phoneUse.push([]);
      emails.push([]);
      emailUse.push([]);
    }

    genders.push(record.gender ? [record.gender] : []);
    DOBs.push(record.birthDate ? [record.birthDate] : []);

    if (record.identifier && record.identifier.length > 0) {
      const filteredidentifier = record.identifier.filter(
        (identifier) => identifier.use !== "old",
      );
      SSNs.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "http://hl7.org/fhir/sid/us-ssn" &&
              identifiers.value,
          )
          .map((ssn) => {
            ssn.isOtherIdentifier = false;
            return ssn.value;
          }),
      );
      MBIs.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "http://hl7.org/fhir/sid/us-mbi" &&
              identifiers.value,
          )
          .map((mbi) => {
            mbi.isOtherIdentifier = false;
            return mbi.value;
          }),
      );

      HICNs.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "http://hl7.org/fhir/sid/us-medicare" &&
              identifiers.value,
          )
          .map((hicn) => {
            hicn.isOtherIdentifier = false;
            return hicn.value;
          }),
      );
      MRNs.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.type &&
              identifiers.type.coding &&
              identifiers.type.coding[0].code &&
              identifiers.type.coding[0].code === "MR" &&
              identifiers.value,
          )
          .map((mrn) => {
            mrn.isOtherIdentifier = false;
            return mrn.value;
          }),
      );
      CPINs.push(
        filteredidentifier
          .filter(
            (identifiers) => identifiers.system === "cpin" && identifiers.value,
          )
          .map((cpin) => {
            cpin.isOtherIdentifier = false;
            return cpin.value;
          }),
      );
      HSIDs.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "healthSafeId" && identifiers.value,
          )
          .map((hsid) => {
            hsid.isOtherIdentifier = false;
            return hsid.value;
          }),
      );
      alternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "alternateMemberId" && identifiers.value,
          )
          .map((briova) => {
            briova.isOtherIdentifier = false;
            return briova.value;
          }),
      );
      briovaExchangeId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "briovaExchangeId" && identifiers.value,
          )
          .map((briova) => {
            briova.isOtherIdentifier = false;
            return briova.value;
          }),
      );

      allSaversAlternateMemberIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "allSaversAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );

      gpsHouseholdIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "gpsHouseholdId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );

      pacificareBehaviorMemberIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "pacificareBehaviorMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );

      riverValleyMemberIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "riverValleyMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      camsAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "camsAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      camsMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "camsMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      cdbXrefIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "cdbXrefId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      cesAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "cesAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      compasId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "compasId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      compasMemberNumber.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "compasMemberNumber" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      cspFacetsAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "cspFacetsAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      employeeId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.type &&
              identifiers.type.coding &&
              identifiers.type.coding[0].code &&
              identifiers.type.coding[0].code === "employeeId" &&
              identifiers.value,
          )
          .map((employeeId) => {
            employeeId.isOtherIdentifier = false;
            return employeeId.value;
          }),
      );
      employerAssignedId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.type &&
              identifiers.type.coding &&
              identifiers.type.coding[0].code &&
              identifiers.type.coding[0].code === "employerAssignedId" &&
              identifiers.value,
          )
          .map((employerAssignedId) => {
            employerAssignedId.isOtherIdentifier = false;
            return employerAssignedId.value;
          }),
      );
      gpsIndividualIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "gpsIndividualId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      icueMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "icueMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      irisAccountNumber.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "irisAccountNumber" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      niceMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "niceMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      optumClinicalServicesAlternateId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "optumClinicalServicesAlternateId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      optumHealthBehavioralServicesAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system ===
                "optumHealthBehavioralServicesAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      oxfordMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "oxfordMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      peoplesHealthMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "peoplesHealthMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      personId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.type &&
              identifiers.type.coding &&
              identifiers.type.coding[0].code &&
              identifiers.type.coding[0].code === "personId" &&
              identifiers.value,
          )
          .map((personIdItem) => {
            personIdItem.isOtherIdentifier = false;
            return personIdItem.value;
          }),
      );
      primeAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "primeAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      rxClaimsMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "rxClaimsMemberId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      rxClaimsSupplementalId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "rxClaimsSupplementalId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      sourceMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.type &&
              identifiers.type.coding &&
              identifiers.type.coding[0].code &&
              identifiers.type.coding[0].code === "sourceMemberId" &&
              identifiers.value,
          )
          .map((sourceMemberIdItem) => {
            sourceMemberIdItem.isOtherIdentifier = false;
            return sourceMemberIdItem.value;
          }),
      );
      studentResourcesAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "studentResourcesAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      studentResourcesId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "studentResourcesId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      umrAlternateMemberId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "umrAlternateMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      ursulaId.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "ursulaId" && identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      driversLicenseNumber.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "driversLicenseNumber" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
      sierraFacetsMemberIds.push(
        filteredidentifier
          .filter(
            (identifiers) =>
              identifiers.system === "sierraFacetsMemberId" &&
              identifiers.value,
          )
          .map((item) => {
            item.isOtherIdentifier = false;
            return item.value;
          }),
      );
    } else {
      SSNs.push([]);
      MBIs.push([]);
      MRNs.push([]);
      HICNs.push([]);
      CPINs.push([]);
      HSIDs.push([]);
      alternateMemberId.push([]);
      briovaExchangeId.push([]);
      allSaversAlternateMemberIds.push([]);
      driversLicenseNumber.push([]);
      sierraFacetsMemberIds.push([]);
      gpsHouseholdIds.push([]);
      pacificareBehaviorMemberIds.push([]);
      riverValleyMemberIds.push([]);
      camsAlternateMemberId.push([]);
      camsMemberId.push([]);
      cdbXrefIds.push([]);
      cesAlternateMemberId.push([]);
      compasId.push([]);
      compasMemberNumber.push([]);
      cspFacetsAlternateMemberId.push([]);
      employeeId.push([]);
      employerAssignedId.push([]);
      gpsIndividualIds.push([]);
      icueMemberId.push([]);
      irisAccountNumber.push([]);
      niceMemberId.push([]);
      optumClinicalServicesAlternateId.push([]);
      optumHealthBehavioralServicesAlternateMemberId.push([]);
      oxfordMemberId.push([]);
      peoplesHealthMemberId.push([]);
      personId.push([]);
      primeAlternateMemberId.push([]);
      rxClaimsMemberId.push([]);
      rxClaimsSupplementalId.push([]);
      sourceMemberId.push([]);
      studentResourcesAlternateMemberId.push([]);
      studentResourcesId.push([]);
      umrAlternateMemberId.push([]);
      ursulaId.push([]);
    }
    if (record.identifier && record.identifier.length > 0) {
      const filteredCoverageIdentifier = record.identifier.filter(
        (identifier) => identifier.use !== "old",
      );
      policyNum.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.policyNum;
          }),
      );
      dependentCd.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.dependentCd;
          }),
      );
      divisionCd.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.divisionCd;
          }),
      );
      relationshipCd.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.relationshipCd;
          }),
      );
      familyID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.familyID;
          }),
      );
      memberID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.memberID;
          }),
      );
      subscriberID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.subscriberID;
          }),
      );
      carrierID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.carrierID;
          }),
      );
      accountID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.accountID;
          }),
      );
      groupID.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.groupID;
          }),
      );
      origSysId.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.origSysId;
          }),
      );
      medicaidRN.push(
        filteredCoverageIdentifier
          .filter((identifiers) => {
            return (
              "type" in identifiers &&
              "coding" in identifiers.type &&
              identifiers.type.coding.some((c) => c.code === "COV")
            );
          })
          .map((coverageidentifier) => {
            const coverageValue = coverageidentifier.value
              ? JSON.parse(coverageidentifier.value)
              : {};
            return coverageValue.medicaidRN;
          }),
      );
    } else {
      policyNum.push([]);
      dependentCd.push([]);
      divisionCd.push([]);
      relationshipCd.push([]);
      familyID.push([]);
      memberID.push([]);
      subscriberID.push([]);
      carrierID.push([]);
      accountID.push([]);
      groupID.push([]);
      origSysId.push([]);
      medicaidRN.push([]);
    }

    const covEndDate = record.extension
      ? record.extension.filter((e) => e.url.includes("coverageEndDate"))
      : [];
    if (covEndDate && covEndDate.length > 0) {
      covEndDate[0].valueDateTime
        ? coverageEndDate.push([
            formattedDateWithDefault(covEndDate[0].valueDateTime),
          ])
        : covEndDate[0].valueString
          ? coverageEndDate.push([
              formattedDateWithDefault(covEndDate[0].valueString),
            ])
          : coverageEndDate.push([]);
    } else {
      coverageEndDate.push([]);
    }
    if (record.eimpLastUpdated) {
      lastUpdated.push([lastUpdatedTimeCell(record.eimpLastUpdated)]);
    } else {
      lastUpdated.push([]);
    }
  }

  // add color information
  const colors = ["#ffffff", "#8DCBE4"];

  const familyNameKeys = [...new Set(familyNames.flat())];
  const givenNameKeys = [...new Set(givenNames.flat())];
  const nameUseKeys = [...new Set(nameUse.flat())];
  const addressLine1Keys = [...new Set(addressLines1.flat())];
  const addressLine2Keys = [...new Set(addressLines2.flat())];
  const cityKeys = [...new Set(cities.flat())];
  const postalCodeKeys = [...new Set(postalCodes.flat())];
  const addressUseKeys = [...new Set(addressUse.flat())];
  const stateCodeKeys = [...new Set(stateCode.flat())];
  const genderKeys = [...new Set(genders.flat())];
  const DOBKeys = [...new Set(DOBs.flat())];
  const phoneKeys = [...new Set(phones.flat())];
  const phoneUseKeys = [...new Set(phoneUse.flat())];
  const emailKeys = [...new Set(emails.flat())];
  const emailUseKeys = [...new Set(emailUse.flat())];
  const SSNKeys = [...new Set(SSNs.flat())];
  const MBIKeys = [...new Set(MBIs.flat())];
  const HICNKeys = [...new Set(HICNs.flat())];
  const CPINKeys = [...new Set(CPINs.flat())];
  const HSIDKeys = [...new Set(HSIDs.flat())];
  const MRNKeys = [...new Set(MRNs.flat())];
  const alternateMemberIdKeys = [...new Set(alternateMemberId.flat())];
  const briovaExchangeIdKeys = [...new Set(briovaExchangeId.flat())];
  const allSaversAlternateMemberIdsKeys = [
    ...new Set(allSaversAlternateMemberIds.flat()),
  ];
  const driversLicenseNumberKeys = [...new Set(driversLicenseNumber.flat())];
  const sierraFacetsMemberIdsKeys = [...new Set(sierraFacetsMemberIds.flat())];
  const gpsHouseholdIdsKeys = [...new Set(gpsHouseholdIds.flat())];
  const pacificareBehaviorMemberIdsKeys = [
    ...new Set(pacificareBehaviorMemberIds.flat()),
  ];
  const riverValleyMemberIdsKeys = [...new Set(riverValleyMemberIds.flat())];
  const camsAlternateMemberIdKeys = [...new Set(camsAlternateMemberId.flat())];
  const camsMemberIdKeys = [...new Set(camsMemberId.flat())];
  const cdbXrefIdsKeys = [...new Set(cdbXrefIds.flat())];
  const cesAlternateMemberIdKeys = [...new Set(cesAlternateMemberId.flat())];
  const compasIdKeys = [...new Set(compasId.flat())];
  const compasMemberNumberKeys = [...new Set(compasMemberNumber.flat())];
  const cspFacetsAlternateMemberIdKeys = [
    ...new Set(cspFacetsAlternateMemberId.flat()),
  ];
  const employeeIdKeys = [...new Set(employeeId.flat())];
  const employerAssignedIdKeys = [...new Set(employerAssignedId.flat())];
  const gpsIndividualIdsKeys = [...new Set(gpsIndividualIds.flat())];
  const icueMemberIdKeys = [...new Set(icueMemberId.flat())];
  const irisAccountNumberKeys = [...new Set(irisAccountNumber.flat())];
  const niceMemberIdKeys = [...new Set(niceMemberId.flat())];
  const optumClinicalServicesAlternateIdKeys = [
    ...new Set(optumClinicalServicesAlternateId.flat()),
  ];
  const optumHealthBehavioralServicesAlternateMemberIdKeys = [
    ...new Set(optumHealthBehavioralServicesAlternateMemberId.flat()),
  ];
  const oxfordMemberIdKeys = [...new Set(oxfordMemberId.flat())];
  const peoplesHealthMemberIdKeys = [...new Set(peoplesHealthMemberId.flat())];
  const personIdKeys = [...new Set(personId.flat())];
  const primeAlternateMemberIdKeys = [
    ...new Set(primeAlternateMemberId.flat()),
  ];
  const rxClaimsMemberIdKeys = [...new Set(rxClaimsMemberId.flat())];
  const rxClaimsSupplementalIdKeys = [
    ...new Set(rxClaimsSupplementalId.flat()),
  ];
  const sourceMemberIdKeys = [...new Set(sourceMemberId.flat())];
  const studentResourcesAlternateMemberIdKeys = [
    ...new Set(studentResourcesAlternateMemberId.flat()),
  ];
  const studentResourcesIdKeys = [...new Set(studentResourcesId.flat())];
  const umrAlternateMemberIdKeys = [...new Set(umrAlternateMemberId.flat())];
  const ursulaIdKeys = [...new Set(ursulaId.flat())];
  const sourceKeys = [...new Set(sources.flat())];
  const policyNumKeys = [...new Set(policyNum.flat())];
  const dependentCodeKeys = [...new Set(dependentCd.flat())];
  const divisionCodeKeys = [...new Set(divisionCd.flat())];
  const relationshipCodeKeys = [...new Set(relationshipCd.flat())];
  const familyIdKeys = [...new Set(familyID.flat())];
  const memberIDKeys = [...new Set(memberID.flat())];
  const subscriberIDKeys = [...new Set(subscriberID.flat())];
  const carrierIDKeys = [...new Set(carrierID.flat())];
  const accountIDKeys = [...new Set(accountID.flat())];
  const groupIDKeys = [...new Set(groupID.flat())];
  const origSysIdKeys = [...new Set(origSysId.flat())];
  const medicaidRNKeys = [...new Set(medicaidRN.flat())];
  const coverageEndDateKeys = [...new Set(coverageEndDate.flat())];
  //const otherIdentifierKeys = [...new Set(otherIdentifiers.flat())];
  const lastUpdatedKeys = [...new Set(lastUpdated.flat())];

  const colorKeyFamilyName = {};
  const colorKeyGivenName = {};
  const colorKeyNameUse = {};
  const colorKeyAddressLine1 = {};
  const colorKeyAddressLine2 = {};
  const colorKeyCities = {};
  const colorKeyPostalCodes = {};
  const colorKeyAddressUse = {};
  const colorKeyStateCode = {};
  const colorKeyGenders = {};
  const colorKeyDOBs = {};
  const colorKeyPhones = {};
  const colorKeyPhoneUse = {};
  const colorKeyEmails = {};
  const colorKeyEmailUse = {};
  const colorKeySSNs = {};
  const colorKeyMBIs = {};
  const colorKeyHICNs = {};
  const colorKeyCPINs = {};
  const colorKeyHSIDs = {};
  const colorKeyMRNs = {};
  const colorKeyAlternateMemberId = {};
  const colorKeyBriovaExchangeId = {};
  const colorKeyAllSaversAlternateMemberIds = {};
  const colorKeyDriversLicenseNumber = {};
  const colorKeySierraFacetsMemberIds = {};
  const colorKeyGpsHouseholdIds = {};
  const colorKeyPacificareBehaviorMemberIds = {};
  const colorKeyRiverValleyMemberIds = {};
  const colorKeyCamsAlternateMemberId = {};
  const colorKeyCamsMemberId = {};
  const colorKeyCdbXrefIds = {};
  const colorKeyCesAlternateMemberId = {};
  const colorKeyCompasId = {};
  const colorKeyCompasMemberNumber = {};
  const colorKeyCspFacetsAlternateMemberId = {};
  const colorKeyEmployeeId = {};
  const colorKeyEmployerAssignedId = {};
  const colorKeyGpsIndividualIds = {};
  const colorKeyIcueMemberId = {};
  const colorKeyIrisAccountNumber = {};
  const colorKeyNiceMemberId = {};
  const colorKeyOptumClinicalServicesAlternateId = {};
  const colorKeyOptumHealthBehavioralServicesAlternateMemberId = {};
  const colorKeyOxfordMemberId = {};
  const colorKeyPeoplesHealthMemberId = {};
  const colorKeyPersonId = {};
  const colorKeyPrimeAlternateMemberId = {};
  const colorKeyRxClaimsMemberId = {};
  const colorKeyRxClaimsSupplementalId = {};
  const colorKeySourceMemberId = {};
  const colorKeyStudentResourcesAlternateMemberId = {};
  const colorKeyStudentResourcesId = {};
  const colorKeyUmrAlternateMemberId = {};
  const colorKeyUrsulaId = {};
  const colorKeySources = {};
  const colorKeypolicyNum = {};
  const colorKeydependentCd = {};
  const colorKeydivisionCd = {};
  const colorKeyrelationshipCd = {};
  const colorKeyfamilyID = {};
  const colorKeymemberID = {};
  const colorKeysubscriberID = {};
  const colorKeycarrierID = {};
  const colorKeyaccountID = {};
  const colorKeygroupID = {};
  const colorKeyorigSysId = {};
  const colorKeymedicaidRN = {};
  const colorKeyCoverageEndDate = {};
  const colorKeyLastUpdated = {};

  const getColor = (index) => {
    if (array.length > 6) {
      return colors[0];
    }
    return colors[index % colors.length];
  };

  const compareAndReturnColor = (arr, colorKeyObj) => {
    if (!isOverlayRecordPage) {
      const normalize = (v) => (v == null || v === "" ? null : v);
      const flattened = arr.reduce((acc, curVal) => {
        return acc.concat(curVal.length > 0 ? curVal.map(normalize) : [null]);
      }, []);
      return new Set(flattened).size > 1 ? colors[0] : colors[1];
    }

    if (colorKeyObj == null) return colors[0];
    const preOverlayValues = new Set(arr[0] || []);
    const result = {};
    Object.keys(colorKeyObj).forEach((val) => {
      result[val] = preOverlayValues.has(val) ? colors[0] : colors[1];
    });
    return result;
  };

  familyNameKeys.forEach((fname, index) => {
    colorKeyFamilyName[fname] = getColor(index);
  });
  givenNameKeys.forEach((gname, index) => {
    colorKeyGivenName[gname] = getColor(index);
  });
  nameUseKeys.forEach((use, index) => {
    colorKeyNameUse[use] = getColor(index);
  });

  addressLine1Keys.forEach((addLine, index) => {
    colorKeyAddressLine1[addLine] = getColor(index);
  });
  addressLine2Keys.forEach((addLine, index) => {
    colorKeyAddressLine2[addLine] = getColor(index);
  });
  cityKeys.forEach((city, index) => {
    colorKeyCities[city] = getColor(index);
  });
  postalCodeKeys.forEach((postalCode, index) => {
    colorKeyPostalCodes[postalCode] = getColor(index);
  });
  addressUseKeys.forEach((use, index) => {
    colorKeyAddressUse[use] = getColor(index);
  });
  stateCodeKeys.forEach((use, index) => {
    colorKeyStateCode[use] = getColor(index);
  });
  genderKeys.forEach((gender, index) => {
    colorKeyGenders[gender] = getColor(index);
  });
  DOBKeys.forEach((dob, index) => {
    colorKeyDOBs[dob] = getColor(index);
  });
  phoneKeys.forEach((phone, index) => {
    colorKeyPhones[phone] = getColor(index);
  });
  phoneUseKeys.forEach((phoneUse, index) => {
    colorKeyPhoneUse[phoneUse] = getColor(index);
  });
  emailKeys.forEach((email, index) => {
    colorKeyEmails[email] = getColor(index);
  });
  emailUseKeys.forEach((emailUse, index) => {
    colorKeyEmailUse[emailUse] = getColor(index);
  });

  SSNKeys.forEach((SSN, index) => {
    colorKeySSNs[SSN] = getColor(index);
  });

  MBIKeys.forEach((MBI, index) => {
    colorKeyMBIs[MBI] = getColor(index);
  });

  HICNKeys.forEach((HICN, index) => {
    colorKeyHICNs[HICN] = getColor(index);
  });

  CPINKeys.forEach((CPIN, index) => {
    colorKeyCPINs[CPIN] = getColor(index);
  });

  HSIDKeys.forEach((HSID, index) => {
    colorKeyHSIDs[HSID] = getColor(index);
  });

  MRNKeys.forEach((MRN, index) => {
    colorKeyMRNs[MRN] = getColor(index);
  });

  alternateMemberIdKeys.forEach((alternateMemberId, index) => {
    colorKeyAlternateMemberId[alternateMemberId] = getColor(index);
  });
  briovaExchangeIdKeys.forEach((briovaId, index) => {
    colorKeyBriovaExchangeId[briovaId] = getColor(index);
  });
  allSaversAlternateMemberIdsKeys.forEach((allSaversId, index) => {
    colorKeyAllSaversAlternateMemberIds[allSaversId] = getColor(index);
  });
  driversLicenseNumberKeys.forEach((dlNumber, index) => {
    colorKeyDriversLicenseNumber[dlNumber] = getColor(index);
  });
  sierraFacetsMemberIdsKeys.forEach((sierraId, index) => {
    colorKeySierraFacetsMemberIds[sierraId] = getColor(index);
  });
  gpsHouseholdIdsKeys.forEach((gpsHouseholdId, index) => {
    colorKeyGpsHouseholdIds[gpsHouseholdId] = getColor(index);
  });
  pacificareBehaviorMemberIdsKeys.forEach((pacificareBehaviorId, index) => {
    colorKeyPacificareBehaviorMemberIds[pacificareBehaviorId] = getColor(index);
  });
  riverValleyMemberIdsKeys.forEach((riverValleyId, index) => {
    colorKeyRiverValleyMemberIds[riverValleyId] = getColor(index);
  });
  camsAlternateMemberIdKeys.forEach((camsAltId, index) => {
    colorKeyCamsAlternateMemberId[camsAltId] = getColor(index);
  });
  camsMemberIdKeys.forEach((camsId, index) => {
    colorKeyCamsMemberId[camsId] = getColor(index);
  });
  cdbXrefIdsKeys.forEach((cdbXrefId, index) => {
    colorKeyCdbXrefIds[cdbXrefId] = getColor(index);
  });
  cesAlternateMemberIdKeys.forEach((cesAltId, index) => {
    colorKeyCesAlternateMemberId[cesAltId] = getColor(index);
  });
  compasIdKeys.forEach((compasId, index) => {
    colorKeyCompasId[compasId] = getColor(index);
  });
  compasMemberNumberKeys.forEach((compasMemberNumber, index) => {
    colorKeyCompasMemberNumber[compasMemberNumber] = getColor(index);
  });
  cspFacetsAlternateMemberIdKeys.forEach((cspFacetsAltId, index) => {
    colorKeyCspFacetsAlternateMemberId[cspFacetsAltId] = getColor(index);
  });
  employeeIdKeys.forEach((employeeId, index) => {
    colorKeyEmployeeId[employeeId] = getColor(index);
  });
  employerAssignedIdKeys.forEach((employerAssignedId, index) => {
    colorKeyEmployerAssignedId[employerAssignedId] = getColor(index);
  });
  gpsIndividualIdsKeys.forEach((gpsIndividualId, index) => {
    colorKeyGpsIndividualIds[gpsIndividualId] = getColor(index);
  });
  icueMemberIdKeys.forEach((icueMemberId, index) => {
    colorKeyIcueMemberId[icueMemberId] = getColor(index);
  });
  irisAccountNumberKeys.forEach((irisAccountNumber, index) => {
    colorKeyIrisAccountNumber[irisAccountNumber] = getColor(index);
  });
  niceMemberIdKeys.forEach((niceMemberId, index) => {
    colorKeyNiceMemberId[niceMemberId] = getColor(index);
  });
  optumClinicalServicesAlternateIdKeys.forEach(
    (optumClinicalServicesAltId, index) => {
      colorKeyOptumClinicalServicesAlternateId[optumClinicalServicesAltId] =
        getColor(index);
    },
  );
  optumHealthBehavioralServicesAlternateMemberIdKeys.forEach(
    (optumHealthBehavioralServicesAltId, index) => {
      colorKeyOptumHealthBehavioralServicesAlternateMemberId[
        optumHealthBehavioralServicesAltId
      ] = getColor(index);
    },
  );
  oxfordMemberIdKeys.forEach((oxfordMemberId, index) => {
    colorKeyOxfordMemberId[oxfordMemberId] = getColor(index);
  });
  peoplesHealthMemberIdKeys.forEach((peoplesHealthMemberId, index) => {
    colorKeyPeoplesHealthMemberId[peoplesHealthMemberId] = getColor(index);
  });
  personIdKeys.forEach((personId, index) => {
    colorKeyPersonId[personId] = getColor(index);
  });
  primeAlternateMemberIdKeys.forEach((primeAltId, index) => {
    colorKeyPrimeAlternateMemberId[primeAltId] = getColor(index);
  });
  rxClaimsMemberIdKeys.forEach((rxClaimsMemberId, index) => {
    colorKeyRxClaimsMemberId[rxClaimsMemberId] = getColor(index);
  });
  rxClaimsSupplementalIdKeys.forEach((rxClaimsSupplementalId, index) => {
    colorKeyRxClaimsSupplementalId[rxClaimsSupplementalId] = getColor(index);
  });
  sourceMemberIdKeys.forEach((sourceMemberId, index) => {
    colorKeySourceMemberId[sourceMemberId] = getColor(index);
  });
  studentResourcesAlternateMemberIdKeys.forEach(
    (studentResourcesAltId, index) => {
      colorKeyStudentResourcesAlternateMemberId[studentResourcesAltId] =
        getColor(index);
    },
  );
  studentResourcesIdKeys.forEach((studentResourcesId, index) => {
    colorKeyStudentResourcesId[studentResourcesId] = getColor(index);
  });
  umrAlternateMemberIdKeys.forEach((umrAltId, index) => {
    colorKeyUmrAlternateMemberId[umrAltId] = getColor(index);
  });
  ursulaIdKeys.forEach((ursulaId, index) => {
    colorKeyUrsulaId[ursulaId] = getColor(index);
  });
  sourceKeys.forEach((source, index) => {
    colorKeySources[source] = getColor(index);
  });
  policyNumKeys.forEach((policyNum, index) => {
    colorKeypolicyNum[policyNum] = getColor(index);
  });
  dependentCodeKeys.forEach((dependentCd, index) => {
    colorKeydependentCd[dependentCd] = getColor(index);
  });
  divisionCodeKeys.forEach((divisionCd, index) => {
    colorKeydivisionCd[divisionCd] = getColor(index);
  });
  relationshipCodeKeys.forEach((relationshipCd, index) => {
    colorKeyrelationshipCd[relationshipCd] = getColor(index);
  });
  familyIdKeys.forEach((familyID, index) => {
    colorKeyfamilyID[familyID] = getColor(index);
  });
  memberIDKeys.forEach((memberID, index) => {
    colorKeymemberID[memberID] = getColor(index);
  });
  subscriberIDKeys.forEach((subscriberID, index) => {
    colorKeysubscriberID[subscriberID] = getColor(index);
  });
  carrierIDKeys.forEach((carrierID, index) => {
    colorKeycarrierID[carrierID] = getColor(index);
  });
  accountIDKeys.forEach((accountID, index) => {
    colorKeyaccountID[accountID] = getColor(index);
  });
  groupIDKeys.forEach((groupID, index) => {
    colorKeygroupID[groupID] = getColor(index);
  });
  origSysIdKeys.forEach((origSysId, index) => {
    colorKeyorigSysId[origSysId] = getColor(index);
  });
  medicaidRNKeys.forEach((medicaidRN, index) => {
    colorKeymedicaidRN[medicaidRN] = getColor(index);
  });
  coverageEndDateKeys.forEach((coverageEndDate, index) => {
    colorKeyCoverageEndDate[coverageEndDate] = getColor(index);
  });

  lastUpdatedKeys.forEach((use, index) => {
    colorKeyLastUpdated[use] = getColor(index);
  });

  const quarantineConfirmDialogUtil = () => {
    return (
      <Modal
        open={quarantineConfirmDialog}
        onClose={() => setQuarantineConfirmDialog(false)}
      >
        <div className={styles.bottomBanner}>
          <h2>Confirmation</h2>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
            On pressing Confirm, you will be directed to the Focus Identity page
            where you need to review the identity.
          </div>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              primary
              onClick={() =>
                applyAndResolveOverlayRecordHandler(
                  array[2],
                  "in_progress",
                  fromSearchOverlay,
                )
              }
            >
              Confirm
            </Button>
            <Button primary onClick={() => setQuarantineConfirmDialog(false)}>
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  // Helper: returns true if at least one record has a non-empty value for this identifier
  const hasData = (identifierArray) =>
    identifierArray.some((recordValues) =>
      Array.isArray(recordValues)
        ? recordValues.length > 0
        : recordValues != null && recordValues !== "",
    );

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          {isOverlayRecordPage && (
            <Flex.Item span={12}>
              <Breadcrumbs>
                <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
                <Breadcrumbs.Item
                  render={() => (
                    <Link to="/review-queue-dashboard">
                      Review Queue Dashboard
                    </Link>
                  )}
                />{" "}
                {!isQuarantineRecord ? "Overlays" : "Quarantine Records"}
              </Breadcrumbs>
            </Flex.Item>
          )}
          <Flex.Item span={12} className={styles.base}>
            <Content className={styles.i}>
              {isOverlayRecordPage && array.length > 0 && (
                <div className={styles.base}>
                  {!isQuarantineRecord ? (
                    <h1>Potential Record Overlay</h1>
                  ) : (
                    <h1>Quarantine Overlay Record</h1>
                  )}
                  <div className={styles.overlaysInfoBar}>
                    <span className={styles.overlaysRecordId}>
                      Record ID:&nbsp;&nbsp; {array[0].id}
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <span className={styles.overlaysSourceId}>
                        Source System:&nbsp;&nbsp;{array[0].meta.source}
                      </span>
                    </span>
                    <span>
                      {" "}
                      {focusIdentityButtonHandler(
                        array[0].id,
                        array[0].meta.source,
                      )}
                    </span>
                  </div>
                </div>
              )}
            </Content>
            <Content>
              {!isOverlayRecordPage && (
                <>
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <h1 style={{ margin: 0 }}>Compare Records</h1>
                      <ButtonList>
                        <Button
                          onClick={() => {
                            if (zoomValue > 0.3) {
                              setZoomValue(zoomValue - 0.1);
                            } else {
                              setZoomWidth(zoomWidth - 1);
                            }
                          }}
                          disabled={zoomWidth < 6}
                        >
                          Zoom Out
                        </Button>
                        <Button
                          onClick={() => {
                            if (zoomWidth < 15) {
                              setZoomWidth(zoomWidth + 1);
                            } else {
                              setZoomValue(zoomValue + 0.1);
                            }
                          }}
                          disabled={zoomValue > 1.5}
                        >
                          Zoom In
                        </Button>
                        <Button
                          onClick={() => {
                            setZoomValue(1.0);
                            setZoomWidth(15);
                          }}
                          disabled={zoomValue === 1.0}
                        >
                          Reset
                        </Button>
                      </ButtonList>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#dbeafe",
                      padding: "0.6rem 1rem",
                      margin: "0.75rem 0 1rem 0",
                      borderRadius: "8px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.4rem",
                        marginRight: "0.6rem",
                        color: "#5BB3D0",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon type="info_filled" size="1.4rem" />
                    </span>
                    <span
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        color: "#1e3a5f",
                      }}
                    >
                      <span style={{ color: "#5BB3D0", fontWeight: 700 }}>
                        Blue-Highlighted{" "}
                      </span>
                      rows show fields with matching values across the compared
                      records.
                    </span>
                  </div>
                </>
              )}
            </Content>
          </Flex.Item>

          {/* <Flex.Item span={12}>
              <div className={styles.base}>
                {isOverlayRecordPage && (
                  <div className={styles.undoContainer}>
                   <button className={styles.iconButton}><Icon type="undo" className={styles.icon} /> Undo All</button> 
                  </div>)}
                </div>
          </Flex.Item> */}

          <Flex.Item span={12}>
            {array.length > 0 ? (
              <>
                <div className={styles.base} style={{ zoom: zoomValue }}>
                  <table
                    className={styles.table}
                    style={{ tableLayout: "auto", width: "100%" }}
                  >
                    <tbody>
                      <tr>
                        {isOverlayRecordPage ? (
                          <th
                            className={styles.th}
                            style={{ fontSize: "1.1rem", fontWeight: 700 }}
                          >
                            Attributes
                          </th>
                        ) : (
                          <th
                            className={styles.th}
                            style={{ fontSize: "1.1rem", fontWeight: 700 }}
                          >
                            Field
                          </th>
                        )}
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            {isOverlayRecordPage ? (
                              <div className={styles.card}>
                                {" "}
                                <span className={styles.overlaysHeadings}>
                                  <b>
                                    {index === 0 && (
                                      <div
                                        className={
                                          styles.overlaysHeadingContainer
                                        }
                                      >
                                        <span
                                          className={styles.overlaysHeading}
                                        >
                                          {isQuarantineRecord
                                            ? "Current Record"
                                            : "Pre-Overlay Values"}
                                        </span>
                                        {!isQuarantineRecord ? (
                                          <button
                                            className={styles.icons}
                                            onClick={() =>
                                              recordOverrideHandler(
                                                "preOverlay",
                                              )
                                            }
                                          >
                                            <Icon type="data" />
                                            <Icon type="arrow_right" />
                                          </button>
                                        ) : null}
                                      </div>
                                    )}{" "}
                                  </b>{" "}
                                  <b>
                                    {index === 1 && (
                                      <div
                                        className={
                                          styles.overlaysHeadingContainer
                                        }
                                      >
                                        <span
                                          className={styles.overlaysHeading}
                                        >
                                          {isQuarantineRecord
                                            ? "Quarantine Record"
                                            : "Post-Overlay Values"}
                                        </span>
                                        {!isQuarantineRecord ? (
                                          <button
                                            className={styles.icons}
                                            onClick={() =>
                                              recordOverrideHandler(
                                                "postOverlay",
                                              )
                                            }
                                          >
                                            <Icon type="data" />
                                            <Icon type="arrow_right" />
                                          </button>
                                        ) : null}
                                      </div>
                                    )}{" "}
                                  </b>{" "}
                                  <b>
                                    {index === 2 && (
                                      <div
                                        className={
                                          styles.overlaysHeadingContainer
                                        }
                                      >
                                        <span
                                          className={styles.overlaysHeading}
                                        >
                                          {isQuarantineRecord
                                            ? "Final Record"
                                            : "Current Value"}
                                        </span>
                                      </div>
                                    )}{" "}
                                  </b>{" "}
                                </span>
                              </div>
                            ) : (
                              <div className={styles.recordHeaderCell}>
                                <div className={styles.recordHeaderCard}>
                                  <div className={styles.recordHeaderHcmId}>
                                    <span
                                      className={styles.recordHeaderHcmLabel}
                                    >
                                      {isImdm ? "EID" : "HCM ID"}{" "}
                                    </span>
                                    {arr.link[0].other.reference}
                                  </div>
                                  <div className={styles.recordHeaderRecordId}>
                                    <span
                                      className={styles.recordHeaderRecordLabel}
                                    >
                                      Record ID{" "}
                                    </span>
                                    {arr.id}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>

                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(sources),
                          }}
                        >
                          Source Code
                        </th>
                        {sources.map((source, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={source}
                              colorKey={compareAndReturnColor(
                                sources,
                                colorKeySources,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaUser />
                            </span>
                            Name
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(familyNames),
                          }}
                        >
                          Last Name
                        </th>
                        {familyNames.map((familyName, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={familyName}
                              colorKey={compareAndReturnColor(
                                familyNames,
                                colorKeyFamilyName,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(givenNames),
                          }}
                        >
                          First Name
                        </th>
                        {givenNames.map((gName, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={gName}
                              colorKey={compareAndReturnColor(
                                givenNames,
                                colorKeyGivenName,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(nameUse),
                          }}
                        >
                          {" "}
                          Use
                        </th>
                        {nameUse.map((use, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={use}
                              colorKey={compareAndReturnColor(
                                nameUse,
                                colorKeyNameUse,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaMapMarkerAlt />
                            </span>
                            Address
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor:
                              compareAndReturnColor(addressLines1),
                          }}
                        >
                          Address Line 1
                        </th>
                        {addressLines1.map((line, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={line}
                              colorKey={compareAndReturnColor(
                                addressLines1,
                                colorKeyAddressLine1,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor:
                              compareAndReturnColor(addressLines2),
                          }}
                        >
                          Address Line 2
                        </th>
                        {addressLines2.map((line, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={line}
                              colorKey={compareAndReturnColor(
                                addressLines2,
                                colorKeyAddressLine2,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(cities),
                          }}
                        >
                          City
                        </th>
                        {cities.map((city, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={city}
                              colorKey={compareAndReturnColor(
                                cities,
                                colorKeyCities,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(stateCode),
                          }}
                        >
                          State
                        </th>
                        {stateCode.map((code, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={code}
                              colorKey={compareAndReturnColor(
                                stateCode,
                                colorKeyStateCode,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(postalCodes),
                          }}
                        >
                          Postal Code
                        </th>
                        {postalCodes.map((code, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={code}
                              colorKey={compareAndReturnColor(
                                postalCodes,
                                colorKeyPostalCodes,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(addressUse),
                          }}
                        >
                          Use
                        </th>
                        {addressUse.map((use, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={use}
                              colorKey={compareAndReturnColor(
                                addressUse,
                                colorKeyAddressUse,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaUser />
                            </span>
                            Basic Demographic
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(genders),
                          }}
                        >
                          Gender
                        </th>
                        {genders.map((gender, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={gender}
                              colorKey={compareAndReturnColor(
                                genders,
                                colorKeyGenders,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(DOBs),
                          }}
                        >
                          Date of Birth
                        </th>
                        {DOBs.map((dob, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={dob}
                              colorKey={compareAndReturnColor(
                                DOBs,
                                colorKeyDOBs,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaPhone />
                            </span>
                            Contact Information
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(phones),
                          }}
                        >
                          Phone
                        </th>
                        {phones.map((phone, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={phone}
                              colorKey={compareAndReturnColor(
                                phones,
                                colorKeyPhones,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(phoneUse),
                          }}
                        >
                          Use
                        </th>
                        {phoneUse.map((use, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={use}
                              colorKey={compareAndReturnColor(
                                phoneUse,
                                colorKeyPhoneUse,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(emails),
                          }}
                        >
                          Email
                        </th>
                        {emails.map((email, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={email}
                              colorKey={compareAndReturnColor(
                                emails,
                                colorKeyEmails,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(emailUse),
                          }}
                        >
                          Use
                        </th>
                        {emailUse.map((use, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={use}
                              colorKey={compareAndReturnColor(
                                emailUse,
                                colorKeyEmailUse,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                      {/* Collapsible Identifier Section */}
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr
                        className={styles.sectionToggleRowTr}
                        onClick={() => setShowIdentifiers((prev) => !prev)}
                        style={{ cursor: "pointer" }}
                      >
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{
                            left: 0,
                            zIndex: 3,
                          }}
                        >
                          <button
                            className={styles.sectionToggleBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowIdentifiers((prev) => !prev);
                            }}
                          >
                            <span className={styles.sectionHeaderIcon}>
                              <FaFingerprint />
                            </span>
                            {showIdentifiers ? "▲" : "▼"} Identifier
                          </button>
                        </th>
                        {array.map((_, idx) => (
                          <td
                            key={idx}
                            className={styles.sectionToggleEmptyCell}
                          />
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>

                      {showIdentifiers && (
                        <>
                          <tr>
                            <th
                              className={`${styles.th} ${styles.shifted}`}
                              style={{
                                backgroundColor: compareAndReturnColor(MBIs),
                                minWidth: zoomWidth + "rem",
                              }}
                            >
                              MedicareBeneficiaryIdentifiers (MBI)
                            </th>
                            {MBIs.map((mbi, index) => (
                              <td
                                key={index}
                                className={styles.td}
                                style={{
                                  minWidth: zoomWidth + "rem",
                                  backgroundColor: compareAndReturnColor(MBIs),
                                }}
                              >
                                <InnerTableComponent
                                  cells={mbi}
                                  colorKey={compareAndReturnColor(
                                    MBIs,
                                    colorKeyMBIs,
                                  )}
                                />
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <th
                              className={`${styles.th} ${styles.shifted}`}
                              style={{
                                backgroundColor: compareAndReturnColor(SSNs),
                                minWidth: zoomWidth + "rem",
                              }}
                            >
                              SocialSecurityNumber (SSN)
                            </th>
                            {SSNs.map((ssn, index) => (
                              <td
                                key={index}
                                className={styles.td}
                                style={{ minWidth: zoomWidth + "rem" }}
                              >
                                <InnerTableComponent
                                  cells={ssn}
                                  colorKey={compareAndReturnColor(
                                    SSNs,
                                    colorKeySSNs,
                                  )}
                                />
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <th
                              className={`${styles.th} ${styles.shifted}`}
                              style={{
                                backgroundColor: compareAndReturnColor(HICNs),
                                minWidth: zoomWidth + "rem",
                              }}
                            >
                              HICN
                            </th>
                            {HICNs.map((hicn, index) => (
                              <td
                                key={index}
                                className={styles.td}
                                style={{ minWidth: zoomWidth + "rem" }}
                              >
                                <InnerTableComponent
                                  cells={hicn}
                                  colorKey={compareAndReturnColor(
                                    HICNs,
                                    colorKeyHICNs,
                                  )}
                                />
                              </td>
                            ))}
                          </tr>
                          {hasData(HSIDs) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(HSIDs),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                HealthSafeId (HSID)
                              </th>
                              {HSIDs.map((hsid, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={hsid}
                                    colorKey={compareAndReturnColor(
                                      HSIDs,
                                      colorKeyHSIDs,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(MRNs) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(MRNs),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                MedicalRecordNumber (MRN)
                              </th>
                              {MRNs.map((mrn, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={mrn}
                                    colorKey={compareAndReturnColor(
                                      MRNs,
                                      colorKeyMRNs,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(alternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(alternateMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                AlternateMemberIds
                              </th>
                              {alternateMemberId.map((altId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={altId}
                                    colorKey={compareAndReturnColor(
                                      alternateMemberId,
                                      colorKeyAlternateMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(allSaversAlternateMemberIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    allSaversAlternateMemberIds,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                AllSaversAlternateMemberIds
                              </th>
                              {allSaversAlternateMemberIds.map(
                                (allSaversId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={allSaversId}
                                      colorKey={compareAndReturnColor(
                                        allSaversAlternateMemberIds,
                                        colorKeyAllSaversAlternateMemberIds,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(gpsHouseholdIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(gpsHouseholdIds),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                GpsHouseholdIds
                              </th>
                              {gpsHouseholdIds.map((gpsHouseholdId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={gpsHouseholdId}
                                    colorKey={compareAndReturnColor(
                                      gpsHouseholdIds,
                                      colorKeyGpsHouseholdIds,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(pacificareBehaviorMemberIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    pacificareBehaviorMemberIds,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                PacificareBehaviorMemberIds
                              </th>
                              {pacificareBehaviorMemberIds.map(
                                (pacificareBehaviorId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={pacificareBehaviorId}
                                      colorKey={compareAndReturnColor(
                                        pacificareBehaviorMemberIds,
                                        colorKeyPacificareBehaviorMemberIds,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(riverValleyMemberIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(riverValleyMemberIds),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                RiverValleyMemberIds
                              </th>
                              {riverValleyMemberIds.map(
                                (riverValleyId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={riverValleyId}
                                      colorKey={compareAndReturnColor(
                                        riverValleyMemberIds,
                                        colorKeyRiverValleyMemberIds,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(briovaExchangeId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(briovaExchangeId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                BriovaExchangeId
                              </th>
                              {briovaExchangeId.map((briovaId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={briovaId}
                                    colorKey={compareAndReturnColor(
                                      briovaExchangeId,
                                      colorKeyBriovaExchangeId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(camsAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    camsAlternateMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CamsAlternateMemberId
                              </th>
                              {camsAlternateMemberId.map((camsAltId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={camsAltId}
                                    colorKey={compareAndReturnColor(
                                      camsAlternateMemberId,
                                      colorKeyCamsAlternateMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(camsMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(camsMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CamsMemberId
                              </th>
                              {camsMemberId.map((camsId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={camsId}
                                    colorKey={compareAndReturnColor(
                                      camsMemberId,
                                      colorKeyCamsMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(cdbXrefIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(cdbXrefIds),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CdbXrefIds
                              </th>
                              {cdbXrefIds.map((cdbXrefId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={cdbXrefId}
                                    colorKey={compareAndReturnColor(
                                      cdbXrefIds,
                                      colorKeyCdbXrefIds,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(cesAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(cesAlternateMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CesAlternateMemberId
                              </th>
                              {cesAlternateMemberId.map((cesAltId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={cesAltId}
                                    colorKey={compareAndReturnColor(
                                      cesAlternateMemberId,
                                      colorKeyCesAlternateMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(compasId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(compasId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CompasId
                              </th>
                              {compasId.map((compasIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={compasIdVal}
                                    colorKey={compareAndReturnColor(
                                      compasId,
                                      colorKeyCompasId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(compasMemberNumber) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(compasMemberNumber),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CompasMemberNumber
                              </th>
                              {compasMemberNumber.map(
                                (compasMemberNumberVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={compasMemberNumberVal}
                                      colorKey={compareAndReturnColor(
                                        compasMemberNumber,
                                        colorKeyCompasMemberNumber,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(cspFacetsAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    cspFacetsAlternateMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                CspFacetsAlternateMemberId
                              </th>
                              {cspFacetsAlternateMemberId.map(
                                (cspFacetsAltId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={cspFacetsAltId}
                                      colorKey={compareAndReturnColor(
                                        cspFacetsAlternateMemberId,
                                        colorKeyCspFacetsAlternateMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(employeeId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(employeeId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                EmployeeId
                              </th>
                              {employeeId.map((employeeIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={employeeIdVal}
                                    colorKey={compareAndReturnColor(
                                      employeeId,
                                      colorKeyEmployeeId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(employerAssignedId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(employerAssignedId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                EmployerAssignedId
                              </th>
                              {employerAssignedId.map(
                                (employerAssignedIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={employerAssignedIdVal}
                                      colorKey={compareAndReturnColor(
                                        employerAssignedId,
                                        colorKeyEmployerAssignedId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(gpsIndividualIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(gpsIndividualIds),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                GpsIndividualIds
                              </th>
                              {gpsIndividualIds.map(
                                (gpsIndividualId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={gpsIndividualId}
                                      colorKey={compareAndReturnColor(
                                        gpsIndividualIds,
                                        colorKeyGpsIndividualIds,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(icueMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(icueMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                IcueMemberId
                              </th>
                              {icueMemberId.map((icueMemberIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={icueMemberIdVal}
                                    colorKey={compareAndReturnColor(
                                      icueMemberId,
                                      colorKeyIcueMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(irisAccountNumber) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(irisAccountNumber),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                IrisAccountNumber
                              </th>
                              {irisAccountNumber.map(
                                (irisAccountNumberVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={irisAccountNumberVal}
                                      colorKey={compareAndReturnColor(
                                        irisAccountNumber,
                                        colorKeyIrisAccountNumber,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(niceMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(niceMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                NiceMemberId
                              </th>
                              {niceMemberId.map((niceMemberIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={niceMemberIdVal}
                                    colorKey={compareAndReturnColor(
                                      niceMemberId,
                                      colorKeyNiceMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(optumClinicalServicesAlternateId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    optumClinicalServicesAlternateId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                OptumClinicalServicesAlternateId
                              </th>
                              {optumClinicalServicesAlternateId.map(
                                (optumClinicalServicesAltId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={optumClinicalServicesAltId}
                                      colorKey={compareAndReturnColor(
                                        optumClinicalServicesAlternateId,
                                        colorKeyOptumClinicalServicesAlternateId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(
                            optumHealthBehavioralServicesAlternateMemberId,
                          ) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    optumHealthBehavioralServicesAlternateMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                OptumHealthBehavioralServicesAlternateMemberId
                              </th>
                              {optumHealthBehavioralServicesAlternateMemberId.map(
                                (optumHealthBehavioralServicesAltId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={optumHealthBehavioralServicesAltId}
                                      colorKey={compareAndReturnColor(
                                        optumHealthBehavioralServicesAlternateMemberId,
                                        colorKeyOptumHealthBehavioralServicesAlternateMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(oxfordMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(oxfordMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                OxfordMemberId
                              </th>
                              {oxfordMemberId.map(
                                (oxfordMemberIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={oxfordMemberIdVal}
                                      colorKey={compareAndReturnColor(
                                        oxfordMemberId,
                                        colorKeyOxfordMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(peoplesHealthMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    peoplesHealthMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                PeoplesHealthMemberId
                              </th>
                              {peoplesHealthMemberId.map(
                                (peoplesHealthMemberIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={peoplesHealthMemberIdVal}
                                      colorKey={compareAndReturnColor(
                                        peoplesHealthMemberId,
                                        colorKeyPeoplesHealthMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(personId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(personId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                PersonId
                              </th>
                              {personId.map((personIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={personIdVal}
                                    colorKey={compareAndReturnColor(
                                      personId,
                                      colorKeyPersonId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(primeAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    primeAlternateMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                PrimeAlternateMemberId
                              </th>
                              {primeAlternateMemberId.map(
                                (primeAltId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={primeAltId}
                                      colorKey={compareAndReturnColor(
                                        primeAlternateMemberId,
                                        colorKeyPrimeAlternateMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(rxClaimsMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(rxClaimsMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                RxClaimsMemberId
                              </th>
                              {rxClaimsMemberId.map(
                                (rxClaimsMemberIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={rxClaimsMemberIdVal}
                                      colorKey={compareAndReturnColor(
                                        rxClaimsMemberId,
                                        colorKeyRxClaimsMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(rxClaimsSupplementalId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    rxClaimsSupplementalId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                RxClaimsSupplementalId
                              </th>
                              {rxClaimsSupplementalId.map(
                                (rxClaimsSupplementalIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={rxClaimsSupplementalIdVal}
                                      colorKey={compareAndReturnColor(
                                        rxClaimsSupplementalId,
                                        colorKeyRxClaimsSupplementalId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(sourceMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(sourceMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                SourceMemberId
                              </th>
                              {sourceMemberId.map(
                                (sourceMemberIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={sourceMemberIdVal}
                                      colorKey={compareAndReturnColor(
                                        sourceMemberId,
                                        colorKeySourceMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(studentResourcesAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    studentResourcesAlternateMemberId,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                StudentResourcesAlternateMemberId
                              </th>
                              {studentResourcesAlternateMemberId.map(
                                (studentResourcesAltId, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={studentResourcesAltId}
                                      colorKey={compareAndReturnColor(
                                        studentResourcesAlternateMemberId,
                                        colorKeyStudentResourcesAlternateMemberId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(studentResourcesId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(studentResourcesId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                StudentResourcesId
                              </th>
                              {studentResourcesId.map(
                                (studentResourcesIdVal, index) => (
                                  <td
                                    key={index}
                                    className={styles.td}
                                    style={{ minWidth: zoomWidth + "rem" }}
                                  >
                                    <InnerTableComponent
                                      cells={studentResourcesIdVal}
                                      colorKey={compareAndReturnColor(
                                        studentResourcesId,
                                        colorKeyStudentResourcesId,
                                      )}
                                    />
                                  </td>
                                ),
                              )}
                            </tr>
                          )}
                          {hasData(umrAlternateMemberId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(umrAlternateMemberId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                UmrAlternateMemberId
                              </th>
                              {umrAlternateMemberId.map((umrAltId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={umrAltId}
                                    colorKey={compareAndReturnColor(
                                      umrAlternateMemberId,
                                      colorKeyUmrAlternateMemberId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(ursulaId) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(ursulaId),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                UrsulaId
                              </th>
                              {ursulaId.map((ursulaIdVal, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={ursulaIdVal}
                                    colorKey={compareAndReturnColor(
                                      ursulaId,
                                      colorKeyUrsulaId,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(driversLicenseNumber) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor:
                                    compareAndReturnColor(driversLicenseNumber),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                DriversLicenseNumber
                              </th>
                              {driversLicenseNumber.map((dlNumber, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={dlNumber}
                                    colorKey={compareAndReturnColor(
                                      driversLicenseNumber,
                                      colorKeyDriversLicenseNumber,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                          {hasData(sierraFacetsMemberIds) && (
                            <tr>
                              <th
                                className={`${styles.th} ${styles.shifted}`}
                                style={{
                                  backgroundColor: compareAndReturnColor(
                                    sierraFacetsMemberIds,
                                  ),
                                  minWidth: zoomWidth + "rem",
                                }}
                              >
                                SierraFacetsMemberIds
                              </th>
                              {sierraFacetsMemberIds.map((sierraId, index) => (
                                <td
                                  key={index}
                                  className={styles.td}
                                  style={{ minWidth: zoomWidth + "rem" }}
                                >
                                  <InnerTableComponent
                                    cells={sierraId}
                                    colorKey={compareAndReturnColor(
                                      sierraFacetsMemberIds,
                                      colorKeySierraFacetsMemberIds,
                                    )}
                                  />
                                </td>
                              ))}
                            </tr>
                          )}
                        </>
                      )}
                      {/* {isImdm} */}
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaStethoscope />
                            </span>
                            HealthCare
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(policyNum),
                            }}
                          >
                            PolicyNum
                          </th>
                          {policyNum.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  policyNum,
                                  colorKeypolicyNum,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(dependentCd),
                            }}
                          >
                            DependentCd
                          </th>
                          {dependentCd.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  dependentCd,
                                  colorKeydependentCd,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(divisionCd),
                            }}
                          >
                            DivisionCd
                          </th>
                          {divisionCd.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  divisionCd,
                                  colorKeydivisionCd,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(relationshipCd),
                            }}
                          >
                            RelationshipCd
                          </th>
                          {relationshipCd.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  relationshipCd,
                                  colorKeyrelationshipCd,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(familyID),
                            }}
                          >
                            FamilyID
                          </th>
                          {familyID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  familyID,
                                  colorKeyfamilyID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(memberID),
                            }}
                          >
                            MemberID
                          </th>
                          {memberID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  memberID,
                                  colorKeymemberID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(subscriberID),
                            }}
                          >
                            SubscriberID
                          </th>
                          {subscriberID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  subscriberID,
                                  colorKeysubscriberID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(carrierID),
                            }}
                          >
                            CarrierID
                          </th>
                          {carrierID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  carrierID,
                                  colorKeycarrierID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(accountID),
                            }}
                          >
                            AccountID
                          </th>
                          {accountID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  accountID,
                                  colorKeyaccountID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(groupID),
                            }}
                          >
                            GroupID
                          </th>
                          {groupID.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  groupID,
                                  colorKeygroupID,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor: compareAndReturnColor(origSysId),
                            }}
                          >
                            OrigSysId
                          </th>
                          {origSysId.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  origSysId,
                                  colorKeyorigSysId,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(medicaidRN),
                            }}
                          >
                            MedicaidRN
                          </th>
                          {medicaidRN.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  medicaidRN,
                                  colorKeymedicaidRN,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      {isImdm && (
                        <tr>
                          <th
                            className={`${styles.th} ${styles.shifted}`}
                            style={{
                              backgroundColor:
                                compareAndReturnColor(coverageEndDate),
                            }}
                          >
                            CoverageEndDate
                          </th>
                          {coverageEndDate.map((line, index) => (
                            <td
                              key={index}
                              className={styles.td}
                              style={{ minWidth: zoomWidth + "rem" }}
                            >
                              <InnerTableComponent
                                cells={line}
                                colorKey={compareAndReturnColor(
                                  coverageEndDate,
                                  colorKeyCoverageEndDate,
                                )}
                              />
                            </td>
                          ))}
                        </tr>
                      )}
                      <tr className={styles.sectionSpacerRow}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr className={styles.sectionToggleRowTr}>
                        <th
                          className={`${styles.th} ${styles.sectionToggleRow}`}
                          style={{ left: 0, zIndex: 3 }}
                        >
                          <span className={styles.sectionHeaderLabel}>
                            <span className={styles.sectionHeaderIcon}>
                              <FaHistory />
                            </span>
                            Meta
                          </span>
                        </th>
                        {array.map((arr, index) => (
                          <td
                            key={index}
                            className={styles.sectionToggleEmptyCell}
                          ></td>
                        ))}
                      </tr>
                      <tr className={styles.sectionSpacerRowBottom}>
                        <td colSpan={array.length + 1}></td>
                      </tr>
                      <tr>
                        <th
                          className={`${styles.th} ${styles.shifted}`}
                          style={{
                            backgroundColor: compareAndReturnColor(lastUpdated),
                          }}
                        >
                          Last Update Time
                        </th>
                        {lastUpdated.map((time, index) => (
                          <td
                            key={index}
                            className={styles.td}
                            style={{ minWidth: zoomWidth + "rem" }}
                          >
                            <InnerTableComponent
                              cells={time}
                              colorKey={compareAndReturnColor(
                                lastUpdated,
                                colorKeyLastUpdated,
                              )}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                {!isOverlayRecordPage && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "12px",
                    }}
                  >
                    <div className={styles.legendBox}>
                      <div className={styles.legendItem}>
                        <span className={styles.legendSquareBlue}></span>
                        <span className={styles.legendLabel}>
                          Matching Values
                        </span>
                      </div>
                      <div className={styles.legendItem}>
                        <span className={styles.legendSquareWhite}></span>
                        <span className={styles.legendLabel}>
                          Different Values
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : !afterRepartitionHasUpdates ? (
              <div
                style={{
                  padding: "2rem",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "2rem",
                }}
              >
                No changes after repartiton.
              </div>
            ) : beforeRepartitionRecords.length > 0 ? (
              <div className={styles.base} style={{ zoom: zoomValue }}>
                <h2 className={styles.h1} style={{ marginBottom: "1rem" }}>
                  Repartition preview:
                </h2>
                <table
                  className={styles.table}
                  style={{ tableLayout: "auto", width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th className={styles.th}></th>
                      <th className={styles.th}>Before Repartition</th>
                      <th className={styles.th}>After Repartition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Golden Record */}
                    <tr>
                      <th
                        className={styles.th}
                        colSpan={3}
                        style={{ background: "#f5f5f5" }}
                      >
                        EnterpriseID: {goldenRecord?.id || ""}
                      </th>
                    </tr>
                    <tr>
                      <th className={styles.th}>&nbsp;&nbsp;&nbsp;Name</th>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;{getPreferredName(goldenRecord, true)}
                      </td>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;
                        {(() => {
                          const afterPatient =
                            afterRepartitionMap[goldenRecord?.id];
                          if (afterPatient)
                            matchedAfterIds.add(goldenRecord.id);
                          return afterPatient
                            ? getPreferredName(afterPatient, true)
                            : getPreferredName(goldenRecord, true);
                        })()}
                      </td>
                    </tr>
                    <tr>
                      <th className={styles.th}>
                        &nbsp;&nbsp;&nbsp;Date of Birth
                      </th>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;{goldenRecord?.birthDate || ""}
                      </td>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;
                        {(() => {
                          const afterPatient =
                            afterRepartitionMap[goldenRecord?.id];
                          return (
                            afterPatient?.birthDate ||
                            goldenRecord?.birthDate ||
                            ""
                          );
                        })()}
                      </td>
                    </tr>
                    <tr>
                      <th className={styles.th}>&nbsp;&nbsp;&nbsp;Gender</th>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;{goldenRecord?.gender || ""}
                      </td>
                      <td
                        className={styles.td}
                        style={{ verticalAlign: "middle" }}
                      >
                        &nbsp;&nbsp;&nbsp;
                        {(() => {
                          const afterPatient =
                            afterRepartitionMap[goldenRecord?.id];
                          return (
                            afterPatient?.gender || goldenRecord?.gender || ""
                          );
                        })()}
                      </td>
                    </tr>
                    {/* Add Records heading */}
                    <tr>
                      <th
                        className={styles.th}
                        colSpan={3}
                        style={{ background: "#f5f5f5" }}
                      >
                        <span style={{ fontWeight: "bold" }}>Records:</span>
                      </th>
                    </tr>
                    {/* Print all record IDs for this golden record (excluding goldenRecord itself) */}
                    {recordEntries.map((record, idx) => (
                      <tr key={record.id || idx}>
                        <th className={styles.th}>
                          &nbsp;&nbsp;&nbsp;{`Record ID${idx + 1}`}
                        </th>
                        <td
                          className={styles.td}
                          style={{ verticalAlign: "middle" }}
                        >
                          &nbsp;&nbsp;&nbsp;{record.id || ""}
                        </td>
                        <td
                          className={styles.td}
                          style={{ verticalAlign: "middle" }}
                        >
                          &nbsp;&nbsp;&nbsp;
                          {!afterRepartitionMap[record.id] ||
                          (afterRepartitionMap[record.id] &&
                            afterRepartitionMap[record.id]._golden_id ===
                              searchedGoldenId)
                            ? record.id
                            : ""}
                        </td>
                      </tr>
                    ))}
                    {/* Render unmatched After Repartition records */}
                    {Object.entries(
                      patients.reduce((acc, { source_entity_id, patient }) => {
                        const goldenId =
                          patient._golden_id || "Unknown Golden ID";
                        if (!acc[goldenId]) acc[goldenId] = [];
                        acc[goldenId].push({ source_entity_id, patient });
                        return acc;
                      }, {}),
                    ).map(([goldenId, records]) => {
                      // Check if the Golden ID is new or existing
                      const isExistingGoldenId = beforeRepartitionRecords.some(
                        (record) => record.id === goldenId,
                      );

                      // Extract survived values (name, dob, gender) from the first record
                      const survivedPatient = records[0]?.patient || {};
                      const survivedName = getPreferredName(survivedPatient);
                      const survivedDob = survivedPatient.birthDate || "";
                      const survivedGender = survivedPatient.gender || "";

                      return (
                        <React.Fragment key={goldenId}>
                          <tr>
                            <th
                              className={styles.th}
                              colSpan={3}
                              style={{ background: "#f5f5f5" }}
                            >
                              <span
                                style={{ fontWeight: "bold", color: "#b8860b" }}
                              >
                                EnterpriseID:
                              </span>
                              <span
                                style={{
                                  background: "#fff7b2",
                                  fontWeight: "bold",
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  marginLeft: "8px",
                                  color: "#333",
                                }}
                              >
                                {goldenId}{" "}
                                {isExistingGoldenId
                                  ? "(Existing EID)"
                                  : "(New EID Created Post Repartition)"}
                              </span>
                            </th>
                          </tr>
                          <tr>
                            <th className={styles.th}>
                              &nbsp;&nbsp;&nbsp;Name
                            </th>
                            <td className={styles.td}>&nbsp;&nbsp;&nbsp;</td>
                            <td
                              className={styles.td}
                              style={{ verticalAlign: "middle" }}
                            >
                              &nbsp;&nbsp;&nbsp;{survivedName}
                            </td>
                          </tr>
                          <tr>
                            <th className={styles.th}>
                              &nbsp;&nbsp;&nbsp;Date of Birth
                            </th>
                            <td className={styles.td}>&nbsp;&nbsp;&nbsp;</td>
                            <td
                              className={styles.td}
                              style={{ verticalAlign: "middle" }}
                            >
                              &nbsp;&nbsp;&nbsp;{survivedDob}
                            </td>
                          </tr>
                          <tr>
                            <th className={styles.th}>
                              &nbsp;&nbsp;&nbsp;Gender
                            </th>
                            <td className={styles.td}>&nbsp;&nbsp;&nbsp;</td>
                            <td
                              className={styles.td}
                              style={{ verticalAlign: "middle" }}
                            >
                              &nbsp;&nbsp;&nbsp;{survivedGender}
                            </td>
                          </tr>
                          {/* Add Records heading */}
                          <tr>
                            <th
                              className={styles.th}
                              colSpan={3}
                              style={{ background: "#f5f5f5" }}
                            >
                              <span
                                style={{ fontWeight: "bold", color: "#b8860b" }}
                              >
                                Records:
                              </span>
                            </th>
                          </tr>
                          {/* Print all record IDs */}
                          {records.map(({ patient }, idx) => (
                            <tr key={patient.id || idx}>
                              <th className={styles.th}>
                                &nbsp;&nbsp;&nbsp;{`Record ID${idx + 1}`}
                              </th>
                              <td className={styles.td}>&nbsp;&nbsp;&nbsp;</td>
                              <td
                                className={styles.td}
                                style={{ verticalAlign: "middle" }}
                              >
                                &nbsp;&nbsp;&nbsp;{patient.id || ""}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>No records to compare</div>
            )}
          </Flex.Item>

          {isOverlayRecordPage &&
            !isQuarantineRecord &&
            !location.pathname.includes("repartition-preview") && (
              <div className={styles.marginAuto}>
                <Flex.Item>
                  <div className={styles.base}>
                    <div></div>
                    <ButtonList className={styles.buttonList}>
                      <div>
                        <Button
                          width="12rem"
                          primary
                          onClick={() =>
                            applyAndResolveOverlayRecordHandler(
                              array[2],
                              fromSearchOverlay,
                            )
                          }
                          isInvisible={checkReadOnlyUser(user)}
                        >
                          Apply Changes
                        </Button>
                      </div>
                      <div>
                        <Button
                          width="12rem"
                          onClick={() =>
                            skipAndReviewHandler("Reviewed", fromSearchOverlay)
                          }
                          isInvisible={checkReadOnlyUser(user)}
                        >
                          Review
                        </Button>
                      </div>
                      {!fromSearchOverlay && (
                        <div>
                          <LinkButton
                            onClick={() => skipAndReviewHandler("Unassigned")}
                          >
                            Skip
                          </LinkButton>
                        </div>
                      )}
                    </ButtonList>
                  </div>
                </Flex.Item>
              </div>
            )}
        </Flex>
      </Container>
      {quarantineConfirmDialogUtil()}
    </Layout>
  );
};

export default CompareRecords;
